import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { TreeLinkStore } from '../store';
import { LocalizationService } from '../../../services/LocalizationService';
import { IAPanel } from '../../panels/Panel';
import { IAButton } from '../../button/Button';
import { TreeTargeting } from '../../treeTargeting/TreeTargeting';
import { IAToggle } from '../../toggle/Toggle';
import { IATextField } from '../../textField/TextField';
import { IAOptionSelector } from '../../optionSelector/OptionSelector';
import { Environment } from '../../../services/Environment';
import Utils from '../../treeTargeting/utils';
import { Helper } from '../../../Helper';
import { ICreatePageFromTemplate } from '../../../interfaces/ICreatePageFromTemplate';
import ContentOrganizerService from '../../../services/ContentOrganizerService';
import { ICreatePageFromTemplateResponse } from '../../../interfaces/ICreatePageFromTemplateResponse';
import { IAImageCropper } from '../../imageCropper/ImageCropper';
import ImageService from '../../../services/ImageService';
import { runInAction } from 'mobx';
import { IAIconPicker } from '../../iconPicker/IconPicker';
import { IAMediaSelector } from '../../mediaSelector/MediaSelector';
import { TreeTargetingForGroups } from '../../treeTargetingForGroups';

export interface IEditPanelProps {
  treeLinkStore?: TreeLinkStore;
  rerender: () => void;
  onSave: () => void;
  onClose: () => void;
}

export interface IEditPanelState {
  showImageCropper: boolean;
  showEditorImageUploader: boolean;
  showEditorImageCropper: boolean;
  cropSettings?: { x: number, y: number, width: number, height: number };
}

@inject('treeLinkStore')
@observer
export class EditPanel extends React.Component<IEditPanelProps, IEditPanelState> {

  private localizationService: LocalizationService = new LocalizationService();

  constructor(props: IEditPanelProps) {
    super(props);
    this.state = {
      showImageCropper: false,
      showEditorImageUploader: false,
      showEditorImageCropper: false,
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  private checkTargetingConfiguration = () => {
    let isProfileData = false;
    let defaultTargetingOperator;
    if (this.props.treeLinkStore?.selectedNode?.targeting) {
      if (this.props.treeLinkStore?.applicationSettings?.applicationSettings?.toLowerCase() === "profile data" &&
        this.props.treeLinkStore?.applicationSettings?.application &&
        this.props.treeLinkStore?.applicationSettings?.application?.default?.type === "profileData") {
        defaultTargetingOperator = "AND";
        isProfileData = true;
        if (!this.props.treeLinkStore?.selectedNode?.targeting?.operator) {
          runInAction(() => {
            if (this.props.treeLinkStore?.selectedNode?.targeting) {
              this.props.treeLinkStore.selectedNode.targeting.operator = defaultTargetingOperator;
            }
          });
        } else if (this.props.treeLinkStore?.selectedNode?.targeting?.operator) {
          defaultTargetingOperator = this.props.treeLinkStore?.selectedNode.targeting.operator;
        }
      } else { // The application is using AD Groups
        defaultTargetingOperator = "OR";
        if (!this.props.treeLinkStore?.selectedNode?.targeting?.operator) {
          runInAction(() => {
            if (this.props.treeLinkStore?.selectedNode?.targeting) {
              this.props.treeLinkStore.selectedNode.targeting.operator = defaultTargetingOperator;
            }
          });
        } else if (this.props.treeLinkStore?.selectedNode?.targeting?.operator) {
          defaultTargetingOperator = this.props.treeLinkStore?.selectedNode.targeting.operator;
        }
      }
    }
    return {
      isProfileData: isProfileData,
      defaultTargetingOperator: defaultTargetingOperator
    };

  }

  private getSelectedValues(): any[] {
    const selectedValues = new Array<any>();
    if (!this.props.treeLinkStore?.applicationSettings || (this.props.treeLinkStore?.applicationSettings && this.props.treeLinkStore?.applicationSettings?.applicationSettings?.toLowerCase() === "profile data")) {
      const targetingFields = this.props.treeLinkStore?.selectedNode?.targeting ? this.props.treeLinkStore?.selectedNode?.targeting?.targetingFields : undefined;
      if (targetingFields) {
        targetingFields.forEach((t) => {
          if (t.values) {
            const targetingField = t;
            targetingField.values.forEach((v) => {
              selectedValues.push({ field: targetingField.field, id: v.id, label: v.label });
            });
          }
        });
      }
    } else {
      if (this.props.treeLinkStore?.selectedNode?.targeting) {
        let groupsData = [];

        // Checking if it should use the GroupData value or Targeting Groups (comes from SDK Functions)
        if (this.props.treeLinkStore?.selectedNode?.targeting?.groupData && this.props.treeLinkStore?.selectedNode?.targeting?.groupData !== "") {
          groupsData = this.props.treeLinkStore?.selectedNode.targeting.groupData.split(";");
        } else if (this.props.treeLinkStore?.selectedNode?.targeting?.targetingGroups?.length > 0) {
          groupsData = this.props.treeLinkStore?.selectedNode.targeting.targetingGroups;
        }

        groupsData.forEach((item) => {
          if (item && item !== "") {
            selectedValues.push({ Label: item, TermGuid: item, id: item });
          }
        });
      }
    }
    return selectedValues;
  }

  private updateTargeting(targetingDataModel: any[]): void {
    const values: Array<{ field: string, values: Array<{ label: string, id: string }> }> = [];

    if (targetingDataModel && targetingDataModel.length > 0) {
      targetingDataModel.forEach((x) => {
        const value = { field: x.field, values: [], id: x.id };

        x.values.forEach((y) => {
          value.values.push({ label: y.label, id: y.id });
        });

        values.push(value);
      });
    }
    runInAction(() => {
      if (this.props.treeLinkStore?.selectedNode?.targeting?.targetingFields) {
        this.props.treeLinkStore.selectedNode.targeting.targetingFields = values;
      }
    });
    this.forceUpdate();
  }

  private save(): void {
    if (this.props.treeLinkStore?.selectedNode?.type === "SPPage") {
      const spPage: ICreatePageFromTemplate = {
        "title": this.props.treeLinkStore?.selectedNode.config.title,
        "template": "Handbook"
      }
      this.props.treeLinkStore?.setSaving(true);
      ContentOrganizerService.createPageFromTemplate(this.props.treeLinkStore.environment, this.props.treeLinkStore?.token, this.props.treeLinkStore?.tenant, this.props.treeLinkStore?.instance.instanceName, spPage).then((response: ICreatePageFromTemplateResponse) => {
        runInAction(() => {
          if (this.props.treeLinkStore?.selectedNode?.config) {
            this.props.treeLinkStore.selectedNode.config.id = response?.id?.toString();
            this.props.treeLinkStore.selectedNode.config.link = response?.url;
          }
        });
        if (this.props.treeLinkStore?.isCreatingNew && this.props.treeLinkStore?.selectedNode?.config?.nodeLevel !== 0) {
          this.props.treeLinkStore?.addNode();
        }
        runInAction(() => {
          if (this.props.treeLinkStore?.selectedNode) {
            this.props.treeLinkStore.selectedNode.type = "Link";
          }
        });
        this.props.onSave();
        this.props.treeLinkStore?.setSaving(false);
        document.body.style.overflowY = "auto";
        document.body.style.height = "inherit";
        this.props.rerender();
      }, (error) => {
        this.props.treeLinkStore?.setSaving(false);
        console.log(error);
      });
    } else {
      if (this.props.treeLinkStore?.isCreatingNew && this.props.treeLinkStore?.selectedNode?.config?.nodeLevel !== 0) {
        this.props.treeLinkStore?.addNode();
      }
      if (this.props.treeLinkStore?.selectedNode?.config?.link === undefined || this.props.treeLinkStore?.selectedNode?.config?.link === "") {
        runInAction(() => {
          if (this.props.treeLinkStore?.selectedNode) {
            this.props.treeLinkStore.selectedNode.type = "Text";
          }
        });
      }
      this.props.onSave();
      document.body.style.overflowY = "auto";
      document.body.style.height = "inherit";
      this.props.rerender();
    }
  }

  private close(): void {
    document.body.style.overflowY = "auto";
    document.body.style.height = "inherit";
    if (this.props.treeLinkStore?.selectedNode?.config?.nodeLevel === 0 && this.props.treeLinkStore?.isCreatingNew) {
      this.props.treeLinkStore?.setIsCreatingNew(false);
      runInAction(() => {
        if (this.props.treeLinkStore?.selectedNode?.config) {
          this.props.treeLinkStore.selectedNode.config.title = "-";
        }
      });
      this.save();
    } else {
      if (!this.props.treeLinkStore?.isCreatingNew) {
        this.props.treeLinkStore?.setTreeLink(Object.assign({}, JSON.parse(this.props.treeLinkStore?.originalTreeLink)));
      }
      this.props.treeLinkStore.selectedNode.config = this.props.treeLinkStore.selectedNodeOriginal?.config;
      this.props.treeLinkStore.selectedNode.targeting = this.props.treeLinkStore.selectedNodeOriginal?.targeting;
      this.props.treeLinkStore.selectedNode.targetingExistingStructure = this.props.treeLinkStore.selectedNodeOriginal?.targetingExistingStructure;
      this.props.treeLinkStore.selectedNode.type = this.props.treeLinkStore.selectedNodeOriginal?.type;
      this.props.treeLinkStore.selectedNode.children = this.props.treeLinkStore.selectedNodeOriginal?.children;
      this.props.rerender();
      this.props.onClose();
      this.forceUpdate();
    }
  }

  public render(): JSX.Element {
    let checkTargetingConfig;
    let defaultTargetingOperator;
    let targetingIsEnabled;
    if (this.props.treeLinkStore?.applicationSettings?.application?.instances) {
      this.props.treeLinkStore?.applicationSettings.application.instances.forEach((instance) => {
        if (instance?.id === this.props.treeLinkStore?.instance.instanceName) {
          targetingIsEnabled = instance.isEnable;
        }
      });
    }
    if (targetingIsEnabled && (this.props.treeLinkStore.selectedNode || this.props.treeLinkStore?.parentNode)) {
      // checkTargetingConfig = this.checkTargetingConfiguration();
      // defaultTargetingOperator = checkTargetingConfig.defaultTargetingOperator;
    }
    let top = 0;
    if (Helper.getDisplayEnvironment() === "Sharepoint" && !this.props.treeLinkStore.isMobile) {
      top = 48;
    }
    if ((Helper.getDisplayEnvironment() === "Sharepoint" || Helper.getDisplayEnvironment() === "SharepointClassic") && document.getElementById("IA_navigator_bar") && !document.getElementById("IA_navigator_vertical_menu") && !this.props.treeLinkStore.isMobile) {
      top = 88;
    }
    return (
      <IAPanel
        panelId={`TreeLinkEditPanel_${this.props.treeLinkStore?.treeLink?.config?.id}`}
        showNavigationBar={true}
        open={this.props.treeLinkStore?.selectedNode != undefined}
        transition={"slide"}
        width={this.props.treeLinkStore.isMobile ? window.innerWidth : 600}
        highlightColor={this.props.treeLinkStore?.editHighlightColor}
        marginTop={top}
        isMobile={this.props.treeLinkStore.isMobile}
        close={() => this.close()}
      >
        <div
          style={{
            padding: "0px 40px 40px 40px",
            boxSizing: "border-box"
          }}
        >
          <IATextField
            id="IA_treeViewEditPanelTitleField"
            style={{ width: "100%" }}
            text={this.props.treeLinkStore.selectedNode ? this.props.treeLinkStore?.selectedNode.config.title : undefined}
            onChange={(title) => {
              runInAction(() => {
                this.props.treeLinkStore.selectedNode.config.title = title;
              });
            }}
            label={this.localizationService.strings.TreeLink_DisplayName}
            borderRadius={5}
          />
          {this.props.treeLinkStore?.selectedNode?.config?.nodeLevel === 0 &&
            <IATextField
              style={{ width: "100%" }}
              text={this.props.treeLinkStore.selectedNode ? this.props.treeLinkStore?.selectedNode?.config?.description : undefined}
              onChange={(description) => {
                runInAction(() => {
                  if (this.props.treeLinkStore.selectedNode?.config) {
                    this.props.treeLinkStore.selectedNode.config.description = description;
                  }
                });
              }}
              label={this.localizationService.strings.TreeLink_Description}
              borderRadius={5}
            />
          }
          {this.props.treeLinkStore?.selectedNode?.config?.nodeLevel === 0 &&
            <div
              style={{
                color: "both",
                float: "left",
                marginTop: 10,
                width: "100%",
                position: "relative"
              }}
            >
              {this.props.treeLinkStore?.selectedNode?.config?.image && this.state.showImageCropper ?
                <IAImageCropper
                  token={this.props.treeLinkStore.token}
                  tenant={this.props.treeLinkStore.tenant}
                  environment={this.props.treeLinkStore.environment}
                  image={ImageService.toDirectUrl(this.props.treeLinkStore?.selectedNode?.config?.image)}
                  showButtons={true}
                  enableZoom={true}
                  initialCrop={this.props.treeLinkStore?.selectedNode?.config?.cropSettings}
                  height={400}
                  onUpdate={(crop) => console.log(crop)}
                  onSave={(crop) => {
                    runInAction(() => {
                      if (this.props.treeLinkStore?.selectedNode?.config) {
                        this.props.treeLinkStore.selectedNode.config.cropSettings = crop;
                      } else {
                        this.props.treeLinkStore.selectedNode.config = {
                          cropSettings: crop
                        };
                      }
                    });
                    this.setState({ showImageCropper: false });
                    this.forceUpdate();
                  }}
                  onCancel={() => this.setState({ showImageCropper: false })}
                />
                :
                <>
                  {this.props.treeLinkStore?.selectedNode?.config?.image &&
                    <IAButton
                      buttonColor="rgba(0,0,0,.3)"
                      mouseOverButtonColor="rgba(0,0,0,.2)"
                      icon="Crop"
                      borderRadius={5}
                      style={{
                        position: "absolute",
                        top: 29,
                        left: 0,
                        zIndex: 1
                      }}
                      onClick={() => this.setState({ showImageCropper: true })}
                    />
                  }
                  <IAMediaSelector
                    label={this.localizationService.strings.TreeLink_Image}
                    isMobile={this.props.treeLinkStore.isMobile}
                    fileType="image"
                    showPreview={!this.props.treeLinkStore?.selectedNode?.config?.cropSettings}
                    highlightColor={this.props.treeLinkStore.editHighlightColor}
                    token={this.props.treeLinkStore.token}
                    tenant={this.props.treeLinkStore.tenant}
                    selectedImage={ImageService.toDirectUrl(this.props.treeLinkStore?.selectedNode?.config?.image)}
                    selectedImageCrop={this.props.treeLinkStore?.selectedNode?.config?.cropSettings}
                    environment={this.props.treeLinkStore.environment}
                    uploadUrl={`${Environment.getEnvironmentForMainAPI(this.props.treeLinkStore.environment)}/api/${this.props.treeLinkStore.tenant}.sharepoint.com/image?site=IntraActive-Messages&relativeUrl=${encodeURIComponent("Lists/PublicRead_MessageAssets")}`}
                    style={{
                      marginBottom: 15
                    }}
                    onChange={(image: string) => {
                      runInAction(() => {
                        if (this.props.treeLinkStore?.selectedNode?.config) {
                          this.props.treeLinkStore.selectedNode.config.image = image;
                          this.props.treeLinkStore.selectedNode.config.cropSettings = undefined;
                        }
                      });
                      this.forceUpdate();
                    }}
                  />
                </>
              }
            </div>
          }
          {this.props.treeLinkStore?.selectedNode?.config?.nodeLevel !== 0 && this.props.treeLinkStore?.showIconOnItems &&
            <IAIconPicker
              label={this.localizationService.strings.TreeLink_SelectIcon}
              selectedIcon={this.props.treeLinkStore?.selectedNode?.config?.icon ? this.props.treeLinkStore?.selectedNode.config.icon : undefined}
              color={Helper.getTextColorBasedOnBackgroundColorDarkness(this.props.treeLinkStore.editHighlightColor, this.props.treeLinkStore?.editHighlightColor, "#333333")}
              showTitle={true}
              select={(icon: string) => {
                runInAction(() => {
                  if (this.props.treeLinkStore?.selectedNode?.config) {
                    this.props.treeLinkStore.selectedNode.config.icon = icon;
                  }
                });
                this.forceUpdate();
              }}
              clear={() => {
                runInAction(() => {
                  if (this.props.treeLinkStore?.selectedNode?.config) {
                    this.props.treeLinkStore.selectedNode.config.icon = undefined;
                  }
                });
              }}
              shouldBeAbleToClear={true}
            />
          }
          <IAOptionSelector
            label={this.localizationService.strings.TreeLink_LinkTo}
            options={[
              {
                icon: "Text",
                key: "Text",
                text: this.localizationService.strings.TreeLink_Text
              },
              {
                icon: "SharePoint",
                key: "SPPage",
                text: this.localizationService.strings.TreeLink_NewPage
              },
              {
                icon: "Link",
                key: "Link",
                text: this.localizationService.strings.TreeLink_ExistingContent
              }
            ]}
            selected={this.props.treeLinkStore.selectedNode ? this.props.treeLinkStore?.selectedNode?.type : "Text"}
            highlightColor={Helper.getTextColorBasedOnBackgroundColorDarkness(this.props.treeLinkStore.editHighlightColor, this.props.treeLinkStore?.editHighlightColor, "#333333")}
            borderRadius={5}
            onChange={(type) => {
              switch (type) {
                case "Link":
                  runInAction(() => {
                    if (this.props.treeLinkStore?.selectedNode?.config) {
                      this.props.treeLinkStore.selectedNode.type = "Link";
                      this.props.treeLinkStore.selectedNode.config.openNewTab = true;
                    }
                  });
                  break;
                case "Text":
                  runInAction(() => {
                    if (this.props.treeLinkStore?.selectedNode) {
                      this.props.treeLinkStore.selectedNode.type = "Text";
                    }
                  });
                  break;
                case "SPPage":
                  runInAction(() => {
                    if (this.props.treeLinkStore?.selectedNode?.config) {
                      this.props.treeLinkStore.selectedNode.type = "SPPage";
                      this.props.treeLinkStore.selectedNode.config.openNewTab = false;
                    }
                  });
                  break;
                default:
                  runInAction(() => {
                    if (this.props.treeLinkStore.selectedNode) {
                      this.props.treeLinkStore.selectedNode.type = "Text"
                    }
                  });
                  break;
              }
            }}
          />
          {this.props.treeLinkStore?.selectedNode?.type === "Link" &&
            <IATextField
              style={{ width: "100%" }}
              text={this.props.treeLinkStore?.selectedNode ? this.props.treeLinkStore?.selectedNode?.config?.link : undefined}
              onChange={(link) => {
                runInAction(() => {
                  if (this.props.treeLinkStore?.selectedNode?.config) {
                    this.props.treeLinkStore.selectedNode.config.link = link;
                  }
                });
              }}
              label={this.localizationService.strings.TreeLink_Address}
              borderRadius={5}
            />
          }
          {this.props.treeLinkStore?.selectedNode?.type !== "Text" &&
            <IAToggle
              style={{ marginTop: 20 }}
              label={this.localizationService.strings.TreeLink_NewTab}
              checked={this.props.treeLinkStore.selectedNode ? this.props.treeLinkStore?.selectedNode?.config?.openNewTab : false}
              color={Helper.getTextColorBasedOnBackgroundColorDarkness(this.props.treeLinkStore.editHighlightColor, this.props.treeLinkStore?.editHighlightColor, "#333333")}
              onChange={() => {
                runInAction(() => {
                  if (this.props.treeLinkStore?.selectedNode?.config) {
                    this.props.treeLinkStore.selectedNode.config.openNewTab = !this.props.treeLinkStore.selectedNode.config.openNewTab;
                  }
                });
              }}
            />
          }
          {targetingIsEnabled &&
            <div style={{
              marginTop: 20,
              float: "left"
            }}>
              {this.props.treeLinkStore?.applicationSettings?.applicationSettings?.toLowerCase() === "profile data" ?
                <TreeTargeting
                  showSelectAll={true}
                  environment={this.props.treeLinkStore.environment}
                  application={"content organizer"}
                  applicationData={this.props.treeLinkStore?.applicationSettings}
                  label={this.localizationService.strings.TargetTree_TargetContent}
                  tenant={`${this.props.treeLinkStore.tenant}.sharepoint.com`}
                  instance={this.props.treeLinkStore.instance.instanceName}
                  authToken={this.props.treeLinkStore.token}
                  apiUrl={Environment.getEnvironmentForMainAPI(this.props.treeLinkStore.environment)}
                  selectedValues={this.getSelectedValues()}
                  showTarget={this.props.treeLinkStore.selectedNode?.targeting?.enabled}
                  onShowTargetChanged={(showTarget) => {
                    let node = this.props.treeLinkStore?.selectedNode;
                    runInAction(() => {
                      if (this.props.treeLinkStore?.selectedNode?.targeting) {
                        this.props.treeLinkStore.selectedNode.targeting.enabled = showTarget;
                        this.props.treeLinkStore.selectedNode.targeting.operator = node?.targeting.operator ? node?.targeting.operator : defaultTargetingOperator;
                        this.props.treeLinkStore.selectedNode.targeting.operator = node?.targeting.operator ? node?.targeting.operator : defaultTargetingOperator;
                      }
                    });
                  }}
                  onChange={(targeting, applicationSettings, groups, _selectAll, targetingExistingStructure) => {
                    if (targeting) {
                      this.updateTargeting(targeting);
                    }
                    if (groups) {
                      runInAction(() => {
                        if (this.props.treeLinkStore.selectedNode?.targeting) {
                          if (groups.length <= 1 && this.props.treeLinkStore?.selectedNode?.targeting) { // If is only one group selected, it should change the operator to be empty
                            this.props.treeLinkStore.selectedNode.targeting.groupData = "";
                          }
                          if (this.props.treeLinkStore?.selectedNode?.targeting) {
                            this.props.treeLinkStore.selectedNode.targeting.groupData = Utils.parseTargetingGroupValues(groups);
                            this.props.treeLinkStore.selectedNode.targeting.targetingGroups = [];
                          }
                          groups.map((g) => {
                            this.props.treeLinkStore?.selectedNode.targeting.targetingGroups.push(g.id.toString());
                          });
                        }
                      });
                    }
                    this.props.treeLinkStore?.setApplicationSettings(applicationSettings);
                    runInAction(() => {
                      if (this.props.treeLinkStore.selectedNode) {
                        this.props.treeLinkStore.selectedNode.targetingExistingStructure = targetingExistingStructure;
                      }
                    });
                  }}
                  operator={defaultTargetingOperator}
                  onLogicalOperatorChanged={(op: string) => {
                    checkTargetingConfig = this.checkTargetingConfiguration();
                    runInAction(() => {
                      if (this.props.treeLinkStore?.selectedNode?.targeting?.operator) {
                        if (checkTargetingConfig.isProfileData) {
                          this.props.treeLinkStore.selectedNode.targeting.operator = op;
                        } else {
                          this.props.treeLinkStore.selectedNode.targeting.operator = op;
                        }
                      }
                    });
                  }}
                  nodeComparison={"label"}
                  highlightColor={this.props.treeLinkStore.editHighlightColor}
                />
                :
                <TreeTargetingForGroups
                  environment={this.props.treeLinkStore.environment}
                  tenant={this.props.treeLinkStore.tenant}
                  application={"content organizer"}
                  instance={this.props.treeLinkStore.instance.instanceName}
                  applicationData={this.props.treeLinkStore?.applicationSettings}
                  selectedGroupIds={this.props.treeLinkStore.selectedNode?.targeting?.targetingGroups}
                  highlightColor={this.props.treeLinkStore.editHighlightColor}
                  showTarget={this.props.treeLinkStore.selectedNode?.targeting?.enabled}
                  onShowTargetChanged={(showTarget) => {
                    let node = this.props.treeLinkStore?.selectedNode;
                    runInAction(() => {
                      if (this.props.treeLinkStore?.selectedNode?.targeting) {
                        this.props.treeLinkStore.selectedNode.targeting.enabled = showTarget;
                        this.props.treeLinkStore.selectedNode.targeting.operator = node?.targeting.operator ? node?.targeting.operator : defaultTargetingOperator;
                        this.props.treeLinkStore.selectedNode.targeting.operator = node?.targeting.operator ? node?.targeting.operator : defaultTargetingOperator;
                      }
                    });
                  }}
                  onGroupIdsUpdated={(ids: number[]) => {
                    runInAction(() => {
                      this.props.treeLinkStore.selectedNode.targeting.targetingGroups = ids;
                      this.forceUpdate();
                      console.log(this.props.treeLinkStore.selectedNode.targeting.targetingGroups);
                    });
                  }}
                />
              }
            </div>
          }
        </div>
        {this.props.treeLinkStore?.selectedNode &&
          <div style={{
            clear: "both",
            float: "left",
            marginBottom: 25
          }}>
            <IAButton
              label={this.props.treeLinkStore.isCreatingNew ? this.localizationService.strings.TreeLink_Create : this.localizationService.strings.TreeLink_EditButton}
              style={{
                marginTop: "40px",
                marginRight: "10px",
                float: "left",
                marginLeft: 40
              }}
              buttonColor={Helper.getTextColorBasedOnBackgroundColorDarkness(this.props.treeLinkStore.editHighlightColor, this.props.treeLinkStore?.editHighlightColor, "#333333")}
              arialable="Save"
              showSpinner={this.props.treeLinkStore.saving}
              disbaled={this.props.treeLinkStore?.selectedNode?.config?.title ? false : true}
              borderRadius={5}
              onClick={() => {
                this.save();
              }}
            />
            <IAButton
              label={this.localizationService.strings.TreeLink_Cancel}
              style={{
                marginTop: "40px",
                marginRight: "10px",
                float: "left"
              }}
              buttonColor="#f4f4f4"
              arialable="Cancel"
              disbaled={false}
              borderRadius={5}
              onClick={() => this.close()}
            />
          </div>
        }
      </IAPanel>
    );
  }
}